import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { XIcon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import { Progress } from "simplydo/react/ui/progress";

// @ts-ignore
import AppIcon from "src/images/appIcon.png";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

const SuspenseContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  img {
    height: 75px;
    min-width: 75px;
  }
  span {
    display: block;
    font-weight: 600;
    opacity: 0.8;
    font-size: 1.3em;
    margin: 12px 0 24px 0;
  }
`;

const NumberOfSeconds = 5;
const NumberOfMilliseconds = NumberOfSeconds * 1000;

const SuspenseLoading = () => {
  const user = useCurrentUser();
  const [msCounted, setMsCounted] = useState(0);
  const [index, setIndex] = useState(0);

  const { t } = useTranslation();

  const loadingMessages = useMemo(
    () => [
      t("loading.suspense.messageOne"),
      t("loading.suspense.messageTwo"),
      t("loading.suspense.messageThree"),
      t("loading.suspense.messageFour"),
      t("loading.suspense.messageFive"),
      t("loading.suspense.messageSix"),
      t("loading.suspense.messageSeven"),
      t("loading.suspense.messageEight"),
      t("loading.suspense.messageNine"),
    ],
    [t],
  );

  const percentageDone = useMemo(
    () => (msCounted / NumberOfMilliseconds) * 100,
    [msCounted],
  );

  const loadingMessage = useMemo(
    () => loadingMessages[index],
    [loadingMessages, index],
  );

  useEffect(() => {
    let start = 0;
    const countUp = setInterval(() => {
      setMsCounted((prevCount) =>
        prevCount >= NumberOfMilliseconds ? prevCount : prevCount + 100,
      );
      start += 100;
      if (start >= 1500) {
        start = 0;
        setIndex(Math.floor(Math.random() * loadingMessages.length));
      }
    }, 100);
    return () => clearInterval(countUp);
  }, [loadingMessages.length]);

  return (
    <SuspenseContainer>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <img src={AppIcon} alt="SimplyDo app icon" />
        {user?.ownerOrganisation?.darkLogoUrl ? (
          <>
            <Icon icon={XIcon} size="large" />
            <img
              src={util.getImageUrl(
                user?.ownerOrganisation,
                "darkLogo",
                "500x500",
              )}
              alt="Owner organisation logo"
            />
          </>
        ) : null}
      </div>
      <span>
        {msCounted >= NumberOfMilliseconds || msCounted <= 300
          ? t("loading.suspense.settingUp", { appName: util.appName() })
          : loadingMessage}
        ..
      </span>
      <Progress
        percent={percentageDone}
        style={{ width: 300 }}
        size="small"
        indicating={msCounted >= NumberOfMilliseconds || msCounted <= 300}
      />
    </SuspenseContainer>
  );
};

export default SuspenseLoading;
